import type { ReactNode } from 'react';

/**
 * Visual primitive shared by Step 3's `<ChannelCard>` and Step 4's
 * `<CarrierCard>` per ADR 0016 — keeps the two card grids
 * pixel-aligned without coupling the channel and carrier domains at
 * the type level. Each domain owns its own catalogue, its own CTA
 * logic, and its own Connect dialog; only the chrome (logo well,
 * title + tagline, status pill, divider, CTA slot) lives here.
 *
 * Layout (vertical stack):
 *   - Pill: absolutely positioned, straddling the top border so a
 *     missing pill doesn't waste vertical space.
 *   - Logo row at the top, centred. Falls back to a muted box when
 *     `logoUrl` is null (used while logo assets land piecemeal — see
 *     ADR 0016 "Risks accepted").
 *   - Title + tagline, centred.
 *   - `mt-auto` pushes the divider + CTA slot to the bottom; with
 *     the `min-h-` enforcing a roomy card, the resulting slack gives
 *     the divider breathing room above and the CTA below.
 *
 * `status` drives the pill + the demoted opacity. Domains supply the
 * CTA via children — Connect button, Disconnect button, "Connected
 * as …" caption, instructions modal trigger, etc.
 */

export type IntegrationCardStatus = 'active' | 'connectable' | 'coming-soon';

interface IntegrationCardProps {
  readonly testId: string;
  /**
   * Logo slot. Caller passes whatever fits in a 48-tall centred row:
   * a plain `<img>`, an img wrapped in a tinted container, a Lucide
   * icon, etc. Keeps the per-domain logo treatment (carrier vs
   * channel) out of the shared primitive.
   */
  readonly logo: ReactNode;
  readonly displayName: string;
  readonly tagline: string;
  readonly status: IntegrationCardStatus;
  readonly children: ReactNode;
}

export function IntegrationCard({
  testId,
  logo,
  displayName,
  tagline,
  status,
  children,
}: IntegrationCardProps) {
  const demoted = status === 'coming-soon';
  const pill = renderStatusPill(status);

  return (
    <div
      data-testid={testId}
      className={
        'border-border bg-card relative flex h-full min-h-[240px] flex-col rounded-md border p-5 ' +
        (demoted ? 'opacity-60' : '')
      }
    >
      {pill ? <div className="absolute right-4 top-0 -translate-y-1/2">{pill}</div> : null}

      <div className="flex h-12 items-center justify-center">{logo}</div>

      <div className="mt-4 space-y-1 text-center">
        <p className="text-foreground text-sm font-semibold">{displayName}</p>
        <p className="text-muted-foreground text-xs leading-relaxed">{tagline}</p>
      </div>

      <div className="border-border mt-auto border-t pt-5">{children}</div>
    </div>
  );
}

/**
 * Pill chrome: white-on-card with a hairline border + small shadow,
 * giving the pill a "floating tag" feel that pops against the card
 * regardless of light / dark mode. Active uses Stripe-style green
 * with a dot; coming-soon uses neutral foreground text;
 * connectable-but-idle gets no pill (nothing to communicate).
 */
function renderStatusPill(status: IntegrationCardStatus) {
  if (status === 'active') {
    return (
      <span className="border-border bg-card inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium text-emerald-700 shadow-sm dark:text-emerald-300">
        <span
          aria-hidden="true"
          className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
        />
        Active
      </span>
    );
  }
  if (status === 'coming-soon') {
    return (
      <span className="border-border bg-card text-foreground inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium shadow-sm">
        Coming soon
      </span>
    );
  }
  return null;
}
